import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from "@mui/material";
import { ArrowDropDownIcon } from "@mui/x-date-pickers";
import { FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState } from "recoil";

import {
  MapRouteBlockTypeEnum,
  mapViewModeAF,
  MapViewModeEnum,
} from "@ryde/shared";

import { MapToolboxViewSelectWrapperStyled } from "./map-toolbox.styled";

interface IMapToolboxViewSelect {
  trackingsCount: number;
  routeCount: number;
  routeBlockType: MapRouteBlockTypeEnum;
}

export const MapToolboxViewSelect: FC<IMapToolboxViewSelect> = ({
  trackingsCount,
  routeCount,
  routeBlockType,
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [selectedValue, setSelectedValue] = useRecoilState(
    mapViewModeAF(routeBlockType),
  );

  const setSelectedValueHandler = useEvent((value: MapViewModeEnum) => {
    setSelectedValue(value);
  });

  const handleClick = (): void => {
    const enumValues = Object.values(MapViewModeEnum);
    const currentIndex = enumValues.indexOf(selectedValue);
    const nextIndex = (currentIndex + 1) % enumValues.length;
    setSelectedValueHandler(enumValues[nextIndex]);
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    value: MapViewModeEnum,
  ): void => {
    event.preventDefault();
    setSelectedValue(value);
    setOpen(false);
  };

  const handleToggle = (): void => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: Event): void => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const optionTextByValue = (value: MapViewModeEnum): string => {
    switch (value) {
      case MapViewModeEnum.TrackingsOnly:
        return t(`selectListLabel.${MapViewModeEnum.TrackingsOnly}`);
      case MapViewModeEnum.RouteOnly:
        return t(`selectListLabel.${MapViewModeEnum.RouteOnly}`);
      default:
        return t(`selectListLabel.${MapViewModeEnum.All}`);
    }
  };

  return (
    <MapToolboxViewSelectWrapperStyled>
      <ButtonGroup
        className="map-view-selector"
        data-e2e="map-view-selector"
        variant="outlined"
        size={"small"}
        ref={anchorRef}
      >
        <Button onClick={handleClick}>
          <span> {optionTextByValue(selectedValue)}</span>
        </Button>
        <Button
          size="small"
          onClick={handleToggle}
          className="map-toolbox-group-button"
        >
          <ArrowDropDownIcon fontSize="small" className="arrow-button" />
        </Button>
      </ButtonGroup>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        transition
        placement="bottom-start"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom-start" ? "left top" : "left bottom",
            }}
          >
            <Paper elevation={8}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem>
                  <MenuItem
                    sx={{ gap: "8px" }}
                    selected={selectedValue === MapViewModeEnum.TrackingsOnly}
                    onClick={event =>
                      handleMenuItemClick(event, MapViewModeEnum.TrackingsOnly)
                    }
                  >
                    <span>
                      {optionTextByValue(MapViewModeEnum.TrackingsOnly)}
                    </span>
                    <strong>{trackingsCount}</strong>
                  </MenuItem>
                  <MenuItem
                    sx={{ gap: "8px" }}
                    selected={selectedValue === MapViewModeEnum.RouteOnly}
                    onClick={event =>
                      handleMenuItemClick(event, MapViewModeEnum.RouteOnly)
                    }
                  >
                    <span>{optionTextByValue(MapViewModeEnum.RouteOnly)}</span>
                    <strong>{routeCount}</strong>
                  </MenuItem>
                  <MenuItem
                    sx={{ gap: "8px" }}
                    selected={selectedValue === MapViewModeEnum.All}
                    onClick={event =>
                      handleMenuItemClick(event, MapViewModeEnum.All)
                    }
                  >
                    <span>{optionTextByValue(MapViewModeEnum.All)}</span>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </MapToolboxViewSelectWrapperStyled>
  );
};
